import React, { useEffect, useMemo, useRef, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import { useThemeObserver } from '@atlaskit/tokens/use-theme-observer';

import { type ProductKeys, ProductKeys as ProductKeysType } from '../../constants/product-keys';
import { useProductConfigs } from '../../constants/products';
import { type ThirdPartyConfigsBootstrap } from '../../constants/schemas/3p-config';
import {
	type ConnectorSource,
	FULL_CONNECTOR_SOURCE,
	SMARTLINKS_CONNECTOR_SOURCE,
} from '../../constants/schemas/3p-config';
import { onBannerDismissed } from '../../utils/events/banner-dismissed';
import { onBannerShown } from '../../utils/events/banner-shown';
import { OAuthSource } from '../../utils/events/oauth-events';
import {
	getFPSGitHubSocialProofBannerCohort,
	isEligibleForFPSGitHubAuthBannerSurfaces,
	serveFPSGitHubAuthBannerPromo,
	serveFPSGitHubSocialProofBannerExperiment,
} from '../../utils/experiments';
import { type MessageContent, OAuthMessage } from '../oauth-message';
import {
	getSocialProofTitleMessageInline,
	OAuthSocialProofMessage,
} from '../oauth-social-proof-message';
import { getOAuthSocialProofMessageContent } from '../oauth-social-proof-message/get-content';

import { messages } from './messages';
import { increaseProductDismissCount, snoozeProduct } from './utils';

const oauthBannerContainerStyles = xcss({
	paddingBottom: 'space.075',
	paddingLeft: 'space.100',
	paddingRight: 'space.100',
});

interface OAuthHandlersProps {
	product: ProductKeys;
	connectorSource: ConnectorSource;
	successFlagTitle: React.ReactNode;
	successFlagDescription?: React.ReactNode;
	showUnderstoodButton?: boolean;
	failFlagTitle: React.ReactNode;
	outboundAuthUrl: string;
	onCompletedAction?: () => void;
	onDismissAction?: () => void;
	isOnSearchDialog?: boolean;
	extraAttributes?: Record<string, unknown>;
}
interface OauthHandlerFunctions {
	onAuthorize: (oAuthSource: OAuthSource) => void;
	onDismiss: () => void;
}

type useOAuthHandlers = (props: OAuthHandlersProps) => OauthHandlerFunctions;

type OAuthBannerProps = {
	product: ProductKeys;
	outboundAuthUrl: string;
	isSmartlink: boolean;
	isOnSearchDialog?: boolean;
	useOAuthHandlers: useOAuthHandlers;
	fireAnalyticsEvent: (payload: any) => void;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	/**
	 * Percentage of the user's team that has already connected this
	 * product. Used by banner with preview to pick the right "social proof" title
	 */
	teamConnectedPercentage?: number;
};

export const OAuthBanner = ({
	product,
	outboundAuthUrl,
	isOnSearchDialog,
	isSmartlink,
	useOAuthHandlers,
	fireAnalyticsEvent,
	thirdPartyConfigs,
	teamConnectedPercentage,
}: OAuthBannerProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const { colorMode } = useThemeObserver();
	const products = useProductConfigs();
	const [isDismissed, setIsDismissed] = useState(false);
	const productString =
		product === 'sharepoint' ? 'SharePoint' : products[product]?.displayName || '';
	const Icon = products[product].Icon;
	const showAlternateConnectMessage = product === ProductKeysType.Figma || isSmartlink;
	const connectorSource = isSmartlink ? SMARTLINKS_CONNECTOR_SOURCE : FULL_CONNECTOR_SOURCE;

	const isFPSGithubBanner = !isOnSearchDialog && product === ProductKeysType.Github;

	const fpsGithubSocialProofBannerCohort = isFPSGithubBanner
		? getFPSGitHubSocialProofBannerCohort({ thirdPartyConfigs })
		: 'control';

	const showOAuthSocialProofMessage =
		isFPSGithubBanner && fpsGithubSocialProofBannerCohort === 'full_social_proof_banner';

	const showOneLineSocialProofTitle =
		isFPSGithubBanner && fpsGithubSocialProofBannerCohort === 'inline_social_proof_banner';

	const isFPSGithubBannerPromo =
		isFPSGithubBanner && serveFPSGitHubAuthBannerPromo({ thirdPartyConfigs });

	// Resolve the connector-specific social-proof config once for both cohorts
	// that need it. Both cohorts pass the connector's success-flag copy
	// (`successFlagTitle`/`successFlagDescription`).
	//   - `full_social_proof_banner` uses only the success-flag copy here; its
	//     other fields (`productIcon`, `description`, `imageSrc`) are resolved
	//     internally by the `<OAuthSocialProofMessage />` it renders.
	//   - `inline_social_proof_banner` additionally reuses its `description`
	//     body copy for the inline banner.
	const socialProofContent =
		showOAuthSocialProofMessage || showOneLineSocialProofTitle
			? getOAuthSocialProofMessageContent(ProductKeysType.Github, { colorMode })
			: undefined;

	const successFlagTitle =
		socialProofContent?.successFlagTitle !== undefined
			? formatMessage(socialProofContent.successFlagTitle, { product: productString })
			: formatMessage(messages.authorizationSuccess, { product: productString });

	const successFlagDescription =
		socialProofContent?.successFlagDescription !== undefined
			? formatMessage(socialProofContent.successFlagDescription)
			: undefined;

	const { onAuthorize, onDismiss } = useOAuthHandlers({
		product,
		connectorSource,
		successFlagTitle,
		successFlagDescription,
		showUnderstoodButton: showOAuthSocialProofMessage,
		failFlagTitle: formatMessage(messages.authorizationFail, {
			product: productString,
		}),
		outboundAuthUrl,
		onCompletedAction: () => {
			setIsDismissed(true);
		},
		onDismissAction: () => {
			setIsDismissed(true);
			if (isFPSGithubBannerPromo) {
				snoozeProduct(product);
			} else {
				increaseProductDismissCount(product);
			}
		},
		isOnSearchDialog,
	});

	useBannerShownAnalytics({
		fireAnalyticsEvent,
		isOnSearchDialog,
		product,
		connectorSource,
	});

	// Fire the `fps_github_social_proof_banner` exposure only for users who can genuinely
	// see the banner (GitHub is eligible). Keying on `isEligibleForExposure` ensures we
	// fire once the GitHub 3P config loads rather than dropping it if `thirdPartyConfigs`
	// is stale on first render; `hasFiredExposureRef` keeps it firing exactly once.
	const hasFiredExposureRef = useRef(false);
	const isEligibleForExposure = useMemo(
		() => isFPSGithubBanner && isEligibleForFPSGitHubAuthBannerSurfaces({ thirdPartyConfigs }),
		[isFPSGithubBanner, thirdPartyConfigs],
	);
	useEffect(() => {
		if (isEligibleForExposure && !hasFiredExposureRef.current) {
			hasFiredExposureRef.current = true;
			serveFPSGitHubSocialProofBannerExperiment(
				{ thirdPartyConfigs },
				{ fireExperimentExposure: true },
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- `thirdPartyConfigs` is omitted as `isEligibleForExposure` is derived from it; the firing render's closure already holds the correct config, and including it would only cause no-op re-runs (guarded by hasFiredExposureRef).
	}, [isEligibleForExposure]);

	// The one-line banner renders the title and description on the same line, so
	// the `inline_social_proof_banner` cohort uses the inline title variants whose
	// terminal punctuation is baked into (and localised within) the message —
	// e.g. `。` in Chinese/Japanese, omitted in Thai — rather than a hard-coded
	// Western full stop, keeping the standalone large-banner heading period-free.
	const socialProofTitleMessage = getSocialProofTitleMessageInline(teamConnectedPercentage ?? 0);

	const title = showOneLineSocialProofTitle ? (
		<FormattedMessage
			{...socialProofTitleMessage}
			values={{ percentage: teamConnectedPercentage ?? 0, product: productString }}
		/>
	) : (
		<FormattedMessage
			{...(showAlternateConnectMessage
				? messages.connectThirdPartyProductTitleAlternate
				: messages.connectThirdPartyProductTitle)}
			values={{ product: productString }}
		/>
	);

	// The `inline_social_proof_banner` cohort (Test2) reuses the connector-specific
	// social-proof description (e.g. GitHub's "Expand your search results to
	// include GitHub PRs, issues, and repos.") — the same body copy as the large
	// social-proof banner — instead of the default connect description.
	const description =
		showOneLineSocialProofTitle && socialProofContent ? (
			<FormattedMessage {...socialProofContent.description} />
		) : (
			<FormattedMessage {...messages.connectThirdPartyProductDescription} />
		);

	if (isDismissed) {
		return null;
	}

	const messageContent: MessageContent = {
		title,
		description,
		connectText: <FormattedMessage {...messages.connect} />,
		MessageIcon: Icon,
	};

	const messageProps = {
		content: messageContent,
		onAuthorize,
		onDismiss: () => {
			fireAnalyticsEvent(
				onBannerDismissed({
					isOnSearchDialog,
					oAuthSource: isOnSearchDialog
						? OAuthSource.quickFindOAuthBanner
						: OAuthSource.searchPageOAuthBanner,
					componentName: 'OAuthBanner',
					product,
					connectorSource,
				}),
			);
			return onDismiss();
		},
	};

	if (showOAuthSocialProofMessage) {
		return (
			<OAuthSocialProofMessage
				product={product}
				onAuthorize={onAuthorize}
				onDismiss={messageProps.onDismiss}
				oAuthSource={OAuthSource.searchPageOAuthBanner}
				teamConnectedPercentage={teamConnectedPercentage}
			/>
		);
	}

	return isOnSearchDialog ? (
		<Box xcss={oauthBannerContainerStyles}>
			<OAuthMessage
				{...messageProps}
				isOnSearchDialog
				oAuthSource={OAuthSource.quickFindOAuthBanner}
			/>
		</Box>
	) : (
		<OAuthMessage {...messageProps} oAuthSource={OAuthSource.searchPageOAuthBanner} />
	);
};

const useBannerShownAnalytics = ({
	fireAnalyticsEvent,
	isOnSearchDialog,
	product,
	connectorSource,
}: {
	fireAnalyticsEvent: (payload: any) => void;
	isOnSearchDialog?: boolean;
	product: ProductKeys;
	connectorSource: ConnectorSource;
}) => {
	useEffect(() => {
		fireAnalyticsEvent(
			onBannerShown({
				isOnSearchDialog,
				oAuthSource: isOnSearchDialog
					? OAuthSource.quickFindOAuthBanner
					: OAuthSource.searchPageOAuthBanner,
				componentName: 'OAuthBanner',
				product,
				connectorSource,
			}),
		);
	}, [fireAnalyticsEvent, isOnSearchDialog, product, connectorSource]);
};
